import styled from 'styled-components';

import { Blurb } from 'components/Containers';
import { formatPhone10Digits } from 'lib/phone';
import { Address } from 'models/schemas/Address';
import { colors } from 'styles/theme/colors';
import { BodyBold, BodySmall } from 'styles/typography';
import AddressIcon from 'svg/AddressIcon';
import PencilIcon from 'svg/PencilIcon';
import PhoneNumberIcon from 'svg/PhoneNumberIcon';

interface PatientContactInformationProps {
  address: Address;
  phone: string;
  showEdit: boolean;
  setShowDeterminantsEdit: (showDeterminantsEdit: boolean) => void;
}

const PatientContactInformation = ({
  address,
  phone,
  showEdit,
  setShowDeterminantsEdit,
}: PatientContactInformationProps) => {
  return (
    <Blurb>
      <Container>
        <Header>
          <StyledBodyBold>Demographic Information</StyledBodyBold>
          {showEdit && (
            <ActionContainer onClick={() => setShowDeterminantsEdit(true)}>
              <PencilIcon width={12} height={12} />
              <BodySmall>Edit</BodySmall>
            </ActionContainer>
          )}
        </Header>
        <InfoContainer>
          <div>
            <AddressIcon />
          </div>
          <div>
            <InfoLabel>Address</InfoLabel>
            {Object.values(address).some((value) => !!value) ? (
              <InfoValue>
                {address.address1 && `${address.address1}`}
                {address.address2 && `, ${address.address2}`}
                {address.city && `, ${address.city}`}
                {address.state && `, ${address.state}`}
                {address.zip && `, ${address.zip}`}
              </InfoValue>
            ) : (
              <InfoValue>&mdash;</InfoValue>
            )}
          </div>
        </InfoContainer>
        <HR />
        <InfoContainer>
          <div>
            <PhoneNumberIcon />
          </div>
          <div>
            <InfoLabel>Phone Number</InfoLabel>
            <InfoValue>{formatPhone10Digits(phone) || <>&mdash;</>}</InfoValue>
          </div>
        </InfoContainer>
      </Container>
    </Blurb>
  );
};

const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${colors.blue100};
  gap: 4px;
  font-size: 12px;
`;

const InfoContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: ${colors.black75};
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 12px;
  color: ${colors.black100};
`;

const StyledBodyBold = styled(BodyBold)`
  font-size: 12px;
`;

const HR = styled.div`
  height: 1px;
  background-color: ${colors.black10};
  margin: 24px 0;
`;

export default PatientContactInformation;
